<template>
  <div class="step">
    <h2>I dati della tua azienda</h2>
    <p class="subtitle text-medium">
      {{ allowAddCompany ? 'Tutti i campi sono obbligatori' : 'Il codice fiscale è obbligatorio' }}
    </p>
    <div class="fieldset">
      <model-list-select
        v-if="isAziendaEstera"
        v-model="params.paeseAzienda"
        :list="paesi"
        option-value="id"
        option-text="label"
        placeholder="Seleziona il paese"
      />
      <Input
        v-else
        v-model="params.fiscalCode"
        type="text"
        :auto-focus="true"
        placeholder="Codice fiscale"
        :regex="regex.fiscalCode"
        :info-message="companyInfoMsg"
        @error="updateErrors"
        @callback="checkCompany"
      />
      <Input
        v-model="params.companyName"
        type="text"
        placeholder="Ragione sociale"
        :disabled="!allowAddCompany"
      />
    </div>
    <div class="actions">
      <button
        class="button primary"
        :disabled="disabled"
        @click="!disabled ? navigate('next') : null"
      >
        Avanti
      </button>
      <button
        class="back"
        @click="navigate('prev')"
      >
        Torna indietro
      </button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import VueLodash from 'vue-lodash';
import lodash from 'lodash';

import { ModelListSelect } from 'vue-search-select';
import Input from '../shared/InputComponent.vue';
import { Paths } from '../../common/paths';
import { GestioneProfili } from '../../common/utenze';

Vue.use(VueLodash, { lodash });

const _ = require('lodash');

export default {
  name: 'DatiAziendali',
  components: {
    ModelListSelect,
    Input,
  },
  props: {
    preset: {
      type: Object,
      default: () => {},
    },
    profileId: {
      type: String,
      required: true,
    },
    isCompleteProfile: {
      type: Boolean,
      default: false,
    },
    isAziendaEstera: {
      type: Boolean,
      default: false,
    },
    preRegistration: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      params: {
        fiscalCode: null,
        michelangeloCode: null,
        companyName: null,
        paeseAzienda: null,
      },
      regex: {
        fiscalCode: [
          {
            rule: /^[A-Za-z]{6}[0-9LMNPQRSTUV]{2}[A-Za-z]{1}[0-9LMNPQRSTUV]{2}[A-Za-z]{1}[0-9LMNPQRSTUV]{3}[A-Za-z]{1}$|^\d{11}$/,
            msg: 'Formato non valido',
          },
        ],
      },
      companyInfoMsg: null,
      errorsCounter: 0,
      canNextStep: false,
      paesi: [],
    };
  },
  computed: {
    isHybridProfile() {
      return _.includes(GestioneProfili.Ibridi, this.profileId);
    },
    nextForProfile() {
      if (_.includes(GestioneProfili.Ibridi, this.profileId) && this.params.michelangeloCode) {
        return false;
      }
      if (!_.includes(GestioneProfili.Ibridi, this.profileId)) {
        return true;
      }
      return true;
    },
    disabled() {
      return this.isCompleteProfile ? !this.validateCompleteProfile : !this.validate;
    },
    validateAziendaEstera() {
      if (!this.isAziendaEstera) {
        return true;
      }
      return !!this.params.paeseAzienda && !!this.params.companyName && !this.errorsCounter;
    },
    validateCompleteProfile() {
      if (this.isAziendaEstera) {
        return this.validateAziendaEstera;
      }
      return (
        this.validate &&
        this.nextForProfile &&
        this.canNextStep
      );
    },
    validate() {
      if (this.isAziendaEstera) {
        return this.validateAziendaEstera;
      }
      return (
        !this.errorsCounter &&
        this.params.fiscalCode &&
        this.params.companyName
      );
    },
    allowAddCompany() {
      if (this.isCompleteProfile) {
        return this.allowAddCompanyCompleteProfile() || this.isAziendaEstera;
      }
      const res =
        (this.isHybridProfile && this.params.michelangeloCode == null) || this.isAziendaEstera;
      return res;
    },
  },
  watch: {
    preset: {
      handler(v) {
        if (!_.isEmpty(v)) {
          this.params = v;
        }
      },
      deep: true,
      immediate: true,
    },
    'params.fiscalCode': function (v) {
      if (v) {
        this.params.fiscalCode = v.toUpperCase();
      }
      this.resetData();
    },
  },
  mounted() {
    this.$emit('push-event');
    if (!this.allowAddCompany && !this.params.michelangeloCode && this.params.fiscalCode) {
      this.checkCompany(this.params.fiscalCode);
    }
    if (this.isAziendaEstera) {
      this.getPaesi();
    }
  },
  methods: {
    allowAddCompanyCompleteProfile() {
      if (this.isHybridProfile && !this.params.michelangeloCode) {
        return true;
      }
      return false;
    },
    resetData() {
      this.params.michelangeloCode = null;
      this.params.companyName = null;
      this.companyInfoMsg = null;
      this.canNextStep = false;
    },
    setCompanyInfoMsg(msg) {
      this.companyInfoMsg = this.allowAddCompany
        ? null
        : msg;
    },
    getPaesi() {
      this.$root.showLoading(true);
      this.$http
        .get(Paths.registrazione.getPaesi)
        .then(response => {
          const { code, values } = response.data;
          if (code == 200) {
            this.paesi = values;
          }
        })
        .catch(error => {})
        .finally(() => {
          this.$root.showLoading(false);
        });
    },
    checkCompany(fc) {
      this.$root.showLoading(true);
      const path = this.preRegistration ? Paths.preRegistrazione.companyName : Paths.registrazione.companyName;
      this.$http
        .post(path, {
          codiceFiscale: fc,
        })
        .then(response => {
          if (response.data.isError) {
            console.error(response.data.message);
          } else if (response.data === 'PF') {
            this.setCompanyInfoMsg('Impossibile registrare persona fisica');
            this.params.companyName = null;
          } else if (response.data === 'CS') {
            this.setCompanyInfoMsg('Impossibile registrare aziende cessate');
            this.params.companyName = null;
          } else if (response.data.ragioneSociale !== null) {
            this.params.companyName = response.data.ragioneSociale;
            this.params.michelangeloCode = response.data.codiceMichelangelo;
            if (this.preRegistration && response.data.code == 701) {
              this.setCompanyInfoMsg(response.data.message);
            }
          } else {
            this.setCompanyInfoMsg(
              'Nessun soggetto trovato. Inserisci un codice fiscale valido presente in Camera di Commercio',
            );
          }
          if (response.data.codiceMichelangelo && this.isHybridProfile && this.isCompleteProfile) {
            this.setCompanyInfoMsg(
              'Stai tentando di registrarti come azienda, cambia profilo',
            );
          }
          this.canNextStep = true;
        })
        .catch(error => {})
        .finally(() => {
          this.$root.showLoading(false);
        });
    },
    navigate(step) {
      const obj = {
        params: this.params,
        step: 'company',
        navigate: step,
      };
      this.$emit('navigate', obj);
    },
    updateErrors(error) {
      this.resetData();
      this.errorsCounter =
        this.errorsCounter > 0 && !error ? (this.errorsCounter -= 1) : (this.errorsCounter += 1);
    },
  },
};
</script>
